<template>
  <div class="w-full pb-10">
    <vx-card class="user-settings-card w-full max-w-5xl mx-auto">
      <div class="p-2 md:p-4 md:px-8">
        
        <!-- Top Section: Profile Pic & Basic Details -->
        <div class="flex flex-col md:flex-row gap-8 md:gap-12 mb-10">
          
          <!-- Left: Profile Picture Upload -->
          <div class="w-full md:w-1/3 flex flex-col items-center mt-2">
            
            <div class="profile-picture-wrapper shadow-sm" @click="$refs.update_avatar_input.click()" id="upload-profile-picture">
              <vs-avatar
                v-if="newProfilePictureUrl"
                class="profile-picture m-0"
                size="160px"
                :src="constants.s3Bucket + '/editor/profile-pictures/' + newProfilePictureUrl"
              />
              <vs-avatar
                v-else-if="localData.profilePictureUrl"
                class="profile-picture m-0"
                size="160px"
                :src="constants.s3Bucket + '/editor/profile-pictures/' + localData.profilePictureUrl"
              />
              <vs-avatar
                v-else
                class="profile-picture m-0"
                size="160px"
                :src="require('@/assets/images/icons/gender/default.png')"
              />
              
              <!-- Persistent Camera Badge -->
              <div class="camera-badge pointer-events-none">
                <feather-icon icon="CameraIcon" svgClasses="w-5 h-5 text-gray-700" />
              </div>
            </div>

            <!-- Description below avatar -->
            <div class="text-center mt-6 px-2 max-w-[250px]">
              <p class="text-sm font-semibold text-gray-800 mb-1">Profile Picture</p>
              <p class="text-xs text-gray-500 leading-relaxed">
                Click to upload. We recommend a 1:1 aspect ratio.<br>
                Max size: 1MB (JPG, PNG)
              </p>
            </div>

            <!-- Cropper Prompt -->
            <vs-prompt
              title="Adjust Profile Picture"
              accept-text="Save"
              @cancel="closeEditProfilePicturePrompt"
              @accept="cropAndUploadProfilePicture"
              @close="closeEditProfilePicturePrompt"
              :active.sync="editProfilePicturePrompt"
            >
              <div class="con-exemple-prompt">
                <clipper-basic :src="tempProfilePicture" ref="clipper"></clipper-basic>
              </div>
            </vs-prompt>
            <input
              type="file"
              class="hidden"
              name="Profile Picture"
              ref="update_avatar_input"
              @change="openEditProfilePicturePrompt"
              accept="image/*"
            />
          </div>

          <!-- Right: Emails, Names, DOB & Gender -->
          <!-- Using flex-col and gap-8 creates perfect, even vertical spacing -->
          <div class="w-full md:w-2/3 flex flex-col gap-8">
            
            <!-- Emails -->
            <div class="flex flex-col md:flex-row gap-8">
              <div class="w-full md:w-1/2">
                <label class="text-sm font-medium text-gray-600 block mb-1">{{ $t('orgDashboard.userDashboard.settings.email') }} (Primary)</label>
                <vs-input class="w-full" disabled :value="userData.email" />
              </div>
              <div class="w-full md:w-1/2">
                <div class="flex justify-between items-end mb-1">
                  <label class="text-sm font-medium text-gray-600">Secondary Email</label>
                  <a 
                    v-if="localData.secondaryEmail" 
                    class="text-primary cursor-pointer text-xs font-semibold hover:underline" 
                    @click="localData.secondaryEmail = ''; newSecondaryEmail = ''"
                  >
                    Change
                  </a>
                </div>
                
                <vs-input
                  v-if="localData.secondaryEmail"
                  class="w-full left-aligned-input"
                  disabled
                  v-model="localData.secondaryEmail"
                />
                
                <vs-input
                  v-else
                  class="w-full left-aligned-input"
                  placeholder="Add secondary email (Press Enter)"
                  v-model="newSecondaryEmail"
                  @keyup.enter="checkAndAddSecondaryEmail"
                />
              </div>
            </div>

            <!-- Names -->
            <div class="flex flex-col md:flex-row gap-8">
              <div class="w-full md:w-1/2">
                <label class="text-sm font-medium text-gray-600 block mb-1">{{ $t('orgDashboard.userDashboard.settings.firstName') }}</label>
                <vs-input class="w-full" v-model="localData.firstName" />
              </div>
              <div class="w-full md:w-1/2">
                <label class="text-sm font-medium text-gray-600 block mb-1">{{ $t('orgDashboard.userDashboard.settings.lastName') }}</label>
                <vs-input class="w-full" v-model="localData.lastName" />
              </div>
            </div>

            <!-- Date of Birth & Gender -->
            <div class="flex flex-col md:flex-row gap-8">
              <div class="w-full md:w-1/2">
                <label class="text-sm font-medium text-gray-600 block mb-1">{{ $t('orgDashboard.userDashboard.settings.dateOfBirth') }}</label>
                <vs-input class="w-full" type="date" v-model="localData.dob" />
              </div>
              
              <div class="w-full md:w-1/2">
                <label class="text-sm font-medium text-gray-600 block mb-1">{{ $t('orgDashboard.userDashboard.settings.gender') }}</label>
                <!-- Added pt-2 (padding-top) to vertically align radios with the input box on the left -->
                <div class="flex items-center gap-6 pt-2">
                  <vs-radio name="Gender" v-model="localData.gender" vs-value="female">
                    <vx-tooltip text="Female" position="bottom">
                      <img src="@/assets/images/icons/gender/female.svg" class="w-6 h-6 hover:scale-110 transition-transform cursor-pointer" />
                    </vx-tooltip>
                  </vs-radio>
                  <vs-radio name="Gender" v-model="localData.gender" vs-value="male">
                    <vx-tooltip text="Male" position="bottom">
                      <img src="@/assets/images/icons/gender/male.svg" class="w-6 h-6 hover:scale-110 transition-transform cursor-pointer" />
                    </vx-tooltip>
                  </vs-radio>
                  <vs-radio name="Gender" v-model="localData.gender" vs-value="other">
                    <vx-tooltip text="Other" position="bottom">
                      <img src="@/assets/images/icons/gender/circle-ring.svg" class="w-6 h-6 hover:scale-110 transition-transform cursor-pointer" />
                    </vx-tooltip>
                  </vs-radio>
                </div>
              </div>
            </div>

          </div>
        </div>

        <vs-divider class="my-6"></vs-divider>

        <!-- Bottom Section: About -->
        <div class="vx-row mb-8">
          <div class="vx-col w-full">
            <label class="text-sm font-medium text-gray-600 block mb-2">{{ $t('orgDashboard.userDashboard.settings.about') }}</label>
            <vs-textarea
              class="w-full shadow-sm"
              counter="200"
              :counter-danger.sync="counterDanger"
              maxlength="200"
              v-model="localData.about"
            />
          </div>
        </div>

        <!-- Save Button -->
        <div class="flex flex-wrap items-center justify-end pt-4 mt-2">
          <vs-button color="#44CC88" class="px-8 py-3 font-semibold shadow-md" @click.stop="saveProfile()">
            {{ $t('orgDashboard.userDashboard.settings.saveChanges') }}
          </vs-button>
        </div>

      </div>
    </vx-card>

    <!-- ========================================== -->
    <!-- MERGE ACCOUNTS POPUP MODAL                 -->
    <!-- ========================================== -->
    <vs-popup title="Merge Accounts Required" :active.sync="mergePromptActive">
      <div class="p-2 text-left">
        <p class="mb-4 text-gray-700">
          The email <strong>{{ newSecondaryEmail }}</strong> is already associated with another GetBoarded profile. 
          To link these accounts, you must merge them.
        </p>
        
        <p class="mb-3 font-semibold text-gray-900">Which profile's basic details (Photo, Bio, Name) do you want to keep?</p>
        
        <div class="mb-5 space-y-3">
          <vs-radio v-model="mergeChoice" :vs-value="userData.email">
            Keep <strong class="text-primary">{{ userData.email }}</strong> (Current Account)
          </vs-radio>
          <br/>
          <vs-radio v-model="mergeChoice" :vs-value="newSecondaryEmail">
            Keep <strong class="text-primary">{{ newSecondaryEmail }}</strong> (Other Account)
          </vs-radio>
        </div>

        <vs-alert color="warning" active="true" class="mb-5 shadow-sm">
          <strong>⚠️ Warning:</strong> The unselected profile's basic details will be permanently deleted. 
          However, all projects, organizations, and other assets from BOTH accounts will be combined!
        </vs-alert>

        <p class="mb-2 text-sm text-danger font-semibold">Type MERGE to confirm this action</p>
        <vs-input class="w-full mb-6" placeholder="Type MERGE" v-model="mergeConfirmationText" />

        <div class="flex justify-end gap-3">
          <vs-button color="danger" type="border" @click="mergePromptActive = false">Cancel</vs-button>
          <vs-button 
            color="primary" 
            :disabled="mergeConfirmationText !== 'MERGE'" 
            @click="executeAccountMerge"
          >
            Confirm Merge
          </vs-button>
        </div>
      </div>
    </vs-popup>

  </div>
</template>

<script>
import axios from "@/axios";
import { successNotifyService, failureNotifyService } from "@/services/errors.service.js";
import constants from "@/constants";

export default {
  data() {
    return {
      counterDanger: false,
      localData: {
        secondaryEmail: ""
      },
      constants,
      editProfilePicturePrompt: false,
      tempProfilePicture: null,
      initialImgInput: null,
      newProfilePictureUrl: null,
      newSecondaryEmail: "",
      mergePromptActive: false,
      mergeChoice: "", 
      mergeConfirmationText: ""
    };
  },
  computed: {
    isUserLoggedIn() {
      return this.$store.state.auth.isUserLoggedIn;
    },
    userData() {
      return this.$store.state.users.data;
    },
    userId() {
      return this.$store.state.auth.user._id;
    },
  },
  watch: {
    userData: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.localData = JSON.parse(JSON.stringify(this.userData));
          if (this.localData.dob) {
            this.localData.dob = this.localData.dob.split("T")[0];
          }
          if (!this.localData.secondaryEmail) {
            this.$set(this.localData, 'secondaryEmail', "");
          }
          
          this.fetchSecondaryEmail();
        }
      },
      immediate: true
    },
  },
  methods: {
    closeEditProfilePicturePrompt() {
      this.$forceUpdate();
    },
    profilePictureValidations(imgInput, maxSize) {
      if (imgInput.target.files && imgInput.target.files[0]) {
        let currentThis = this;

        if (imgInput.target.files > 1) {
          return;
        }
        const parts = imgInput.target.files[0].name.split(".");
        const ext = parts[parts.length - 1];

        if (ext !== "jpg" && ext !== "jpeg" && ext !== "png") {
          failureNotifyService({
            title: "Incorrect File Type",
            text: "Please upload a supported image file type, e.g. jpg, png, etc.",
            currentThis,
          });
          return;
        }

        if (imgInput.target.files[0].size / (1024 * 1024) > maxSize) {
          failureNotifyService({
            title: "Image File Size Exceeded",
            text: "The size of the profile picture should be less than 1MB.",
            currentThis,
          });
          return;
        }

        return true;
      }
    },
    openEditProfilePicturePrompt(input) {
      if (!this.profilePictureValidations(input, 1)) return; 

      this.initialImgInput = input;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.tempProfilePicture = e.target.result;
        this.editProfilePicturePrompt = true;
      };
      reader.readAsDataURL(input.target.files[0]);
    },
    cropAndUploadProfilePicture() {
      const currentThis = this;
      const canvas = this.$refs.clipper.clip();
      canvas.toBlob(function (blob) {
        const reader = new FileReader();
        reader.onload = (e) => {
          currentThis.updateImageService(e.target.result)
        };
        reader.readAsDataURL(blob);
      });
    },
    updateImageService(finalImgData) {
      const _id = this.userId;

      this.$store.commit("editor/TOGGLE_SAVE_IN_PROGRESS", true);

      this.$vs.loading({
        container: "#upload-profile-picture",
        color: "primary",
        background: "white",
        scale: 0.45,
      });

      const profilePictureData = finalImgData;
      const profilePictureUrl = _id + "-" + this.initialImgInput.target.files[0].name;

      const ppObj = {
        profilePictureData,
        profilePictureUrl,
      };

      this.$store
        .dispatch("users/updateProfilePicture", { ppObj, _id })
        .then((res) => {
          this.$store.commit("editor/TOGGLE_SAVE_IN_PROGRESS", false);
          this.newProfilePictureUrl = res.data.profilePictureUrl;
          this.$forceUpdate();
          this.$vs.loading.close("#upload-profile-picture > .con-vs-loading");
        })
        .catch((err) => {
          throw err;
        });
    },

    async checkAndAddSecondaryEmail() {
      const pyApiUrl = process.env.VUE_APP_API_URL;
      if (!this.newSecondaryEmail) return;

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.newSecondaryEmail)) {
        failureNotifyService({ currentThis: this, title: 'Invalid Email', text: 'Please enter a valid email address.' });
        return;
      }

      if (this.newSecondaryEmail === this.userData.email) {
        failureNotifyService({ currentThis: this, title: 'Error', text: 'This is already your primary email.' });
        return;
      }

      this.$vs.loading();
      
      try {
        const response = await axios.post(`${pyApiUrl}/api/settings/check-email`, { 
          email: this.newSecondaryEmail 
        });

        if (response.data.status === 'exists') {
          this.$vs.loading.close();
          this.mergeConfirmationText = "";
          this.mergePromptActive = true;
        } else {
          await axios.post(`${pyApiUrl}/api/settings/secondary-email`, {
            user_id: this.userId,
            secondary_email: this.newSecondaryEmail
          });
          
          this.$vs.loading.close();
          this.localData.secondaryEmail = this.newSecondaryEmail;
          successNotifyService({ currentThis: this, title: 'Email Added', text: 'Secondary email added successfully.' });
          this.newSecondaryEmail = ""; 
        }
      } catch (error) {
        this.$vs.loading.close();
        failureNotifyService({ currentThis: this, title: 'Error', text: 'Failed to check or add email.' });
      }
    },

    fetchSecondaryEmail() {
      if (!this.userId) return;
      const pyApiUrl = process.env.VUE_APP_API_URL;
      
      axios.get(`${pyApiUrl}/api/settings/secondary-email/${this.userId}`)
        .then(res => {
          if (res.data.status === 'success' && res.data.secondary_email) {
            this.$set(this.localData, 'secondaryEmail', res.data.secondary_email);
          }
        })
        .catch(err => console.error("Failed to fetch secondary email", err));
    },

    async executeAccountMerge() {
      const pyApiUrl = process.env.VUE_APP_API_URL;
      this.mergePromptActive = false;
      this.$vs.loading({ text: 'Merging Accounts... This may take a moment.' });

      try {
        let payload = {
          primary_email: this.mergeChoice, 
          secondary_email: this.mergeChoice === this.userData.email ? this.newSecondaryEmail : this.userData.email
        };

        const response = await axios.post(`${pyApiUrl}/api/settings/merge-accounts`, payload);

        this.$vs.loading.close();

        if (response.data.status === 'success') {
          successNotifyService({ currentThis: this, title: 'Accounts Merged!', text: 'Your accounts and data have been successfully combined.' });
          this.localData.secondaryEmail = this.newSecondaryEmail;
          this.newSecondaryEmail = ""; 
        }
      } catch (error) {
        this.$vs.loading.close();
        failureNotifyService({ 
          currentThis: this, 
          title: 'Merge Failed', 
          text: error.response?.data?.detail || 'An error occurred during account merge.' 
        });
      }
    },

    saveProfile() {
      const payload = {
        _id: this.userId,
        firstName: this.localData.firstName,
        lastName: this.localData.lastName,
        dob: this.localData.dob,
        gender: this.localData.gender,
        about: this.localData.about,
        secondaryEmails: this.localData.secondaryEmails
      };

      this.$store.dispatch('users/saveProfile', payload).then(() => {
        successNotifyService({currentThis: this, title: 'Profile Updated Successfully', text: 'Your profile has been updated successfully'})
      }).catch(err => {
        throw err;
      })
    },
  }
};
</script>

<style scoped>
/* Reset aligns */
* {
  text-align: left;
}
.text-center {
  text-align: center;
}

/* Custom Upload Avatar */
.profile-picture-wrapper {
  position: relative;
  width: 160px;
  height: 160px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border-radius: 50%;
  border: 1px solid #ebedf0;
  background-color: #f8f9fa;
  overflow: visible;
}

.profile-picture {
  position: relative;
  width: 160px !important;
  height: 160px !important;
}

/* Persistent Camera Badge styling */
.camera-badge {
  position: absolute;
  bottom: 5px;
  right: 5px;
  background-color: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  transition: transform 0.2s, background-color 0.2s;
  z-index: 10;
}

.profile-picture-wrapper:hover .camera-badge {
  background-color: #f3f4f6;
  transform: scale(1.05);
}

.profile-picture-wrapper:hover {
  opacity: 0.9;
}

/* About Textarea sizing */
::v-deep .vs-textarea {
  background: #fbfbfc;
  border-radius: 8px; /* Slightly rounder corners */
  border: 1px solid #e2e8f0; /* Softer border */
  height: 180px !important; 
  color: #4a5568;
}

::v-deep .vs-input--input {
  background: #fbfbfc !important;
  border: 1px solid #e2e8f0; /* Added subtle borders to inputs to match modern UI */
  border-radius: 6px;
}
</style>